import { supabase } from '../lib/supabase'
import type { Shoot } from '../types/portal'
import type { WriteResult } from './projectWrites'

/**
 * A project's shoot days.
 *
 * Until 12 Sep 2026 a project had exactly one shoot: a single date, time and
 * location on the project row. Real productions are not shaped like that —
 * ATM TV LIVE is a build day, a test day and then four days of event. Five
 * entries in the company calendar, one date in Portal OS, which is precisely
 * why Portal OS read as the thinner record of the two.
 *
 * `project_shoots` is the real list. `projects.next_shoot_date` and its two
 * companions still exist, but are now DERIVED from this table by a database
 * trigger — "next" meaning the earliest shoot that has not finished, or the
 * most recent one if they are all past. Everything written before this change
 * (the client Schedule page, the Call Sheet Builder, Week Ahead, the calendar
 * sync) still reads those columns and still works, unchanged.
 *
 * The consequence worth knowing: never write next_shoot_date directly. The
 * mirror would overwrite it — a save that looks like it worked and quietly
 * undoes itself. `set_project_next_shoot` (see utils/projectWrites.ts) is the
 * way Project Settings' single date field edits the shoot it refers to; the
 * Calendar tab writes the rows here directly.
 */

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function fail(error: { message: string; code?: string } | null): WriteResult {
  if (!error) return { ok: true }
  const detail = `project_shoots: ${error.message}${error.code ? ` (${error.code})` : ''}`
  console.error(`[supabase] write refused — ${detail}`)
  return { ok: false, error: detail }
}

export function shootOf(row: Record<string, unknown>): Shoot {
  return {
    id: String(row.id ?? ''),
    date: text(row.start_date).slice(0, 10),
    /* Absent means a single day. Present means a run, inclusive of both ends. */
    endDate: text(row.end_date).slice(0, 10) || undefined,
    title: text(row.title),
    time: text(row.time_label),
    city: '',
    location: text(row.location),
    details: [],
  }
}

/** True while any part of the shoot is still to come — a run counts until its last day. */
export function shootIsUpcoming(shoot: Shoot, today: Date = new Date()): boolean {
  const last = shoot.endDate || shoot.date
  if (!last) return false
  const midnight = new Date(today)
  midnight.setHours(0, 0, 0, 0)
  const parsed = new Date(`${last}T00:00:00`)
  return !Number.isNaN(parsed.getTime()) && parsed.getTime() >= midnight.getTime()
}

/** Every ISO date a shoot covers, so a run can be drawn across a month grid. */
export function shootDates(shoot: Shoot): string[] {
  const start = new Date(`${shoot.date}T00:00:00`)
  if (Number.isNaN(start.getTime())) return []
  const end = shoot.endDate ? new Date(`${shoot.endDate}T00:00:00`) : start
  if (Number.isNaN(end.getTime()) || end < start) return [shoot.date]

  const out: string[] = []
  const cursor = new Date(start)
  /* Guarded rather than trusted: a typo of 2206 instead of 2026 should not
     spin here. Three months is far longer than any real shoot. */
  while (cursor <= end && out.length < 100) {
    out.push(isoOf(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return out
}

/** Local-date ISO (YYYY-MM-DD). Never toISOString(), which shifts to UTC. */
export function isoOf(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const COLUMNS = 'id, project_id, start_date, end_date, time_label, title, location, order_index'

/** Every shoot day on one project, earliest first. */
export async function fetchProjectShoots(projectId: string): Promise<Shoot[]> {
  if (!supabase || !projectId) return []

  const { data, error } = await supabase
    .from('project_shoots')
    .select(COLUMNS)
    .eq('project_id', projectId)
    .order('start_date', { ascending: true })
    .order('order_index', { ascending: true })

  if (error) {
    console.warn(`[supabase] project shoots load failed: ${error.message}`)
    return []
  }
  return (data ?? []).map((row) => shootOf(row as Record<string, unknown>))
}

/**
 * Every shoot day across every project the caller can see, grouped by project.
 *
 * One query rather than one per project: the studio list loads all projects at
 * once, and this table is small — a handful of rows per project, most of them
 * in the past.
 */
export async function fetchShootsByProject(): Promise<Map<string, Shoot[]>> {
  const grouped = new Map<string, Shoot[]>()
  if (!supabase) return grouped

  const { data, error } = await supabase
    .from('project_shoots')
    .select(COLUMNS)
    .order('start_date', { ascending: true })
    .order('order_index', { ascending: true })

  if (error) {
    console.warn(`[supabase] project shoots list failed: ${error.message}`)
    return grouped
  }

  for (const row of data ?? []) {
    const record = row as Record<string, unknown>
    const projectId = String(record.project_id ?? '')
    if (!projectId) continue
    const list = grouped.get(projectId) ?? []
    list.push(shootOf(record))
    grouped.set(projectId, list)
  }
  return grouped
}

export interface ShootDraft {
  date: string
  endDate?: string
  title: string
  time: string
  location: string
}

function rowOf(draft: ShootDraft) {
  return {
    start_date: draft.date,
    /* A run that ends on its start date is just a day — stored as one, so
       nothing downstream has to special-case the equal case. */
    end_date: draft.endDate && draft.endDate > draft.date ? draft.endDate : null,
    title: draft.title.trim() || null,
    time_label: draft.time.trim() || null,
    location: draft.location.trim() || null,
  }
}

/**
 * Adds a shoot day. The database trigger re-derives the project's
 * `next_shoot_*` mirror afterwards, so nothing here writes it.
 */
export async function createShoot(
  projectId: string,
  draft: ShootDraft,
): Promise<{ result: WriteResult; shoot?: Shoot }> {
  if (!supabase) return { result: { ok: false, error: 'Supabase is not configured.' } }

  const { data, error } = await supabase
    .from('project_shoots')
    .insert({ project_id: projectId, ...rowOf(draft) })
    .select(COLUMNS)
    .single()

  const result = fail(error)
  if (!result.ok || !data) return { result }
  return { result, shoot: shootOf(data as Record<string, unknown>) }
}

export async function updateShoot(id: string, draft: ShootDraft): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase.from('project_shoots').update(rowOf(draft)).eq('id', id)
  return fail(error)
}

export async function deleteShoot(id: string): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const { error } = await supabase.from('project_shoots').delete().eq('id', id)
  return fail(error)
}
